import React from "react";
import { type ProductCard } from "types/product";
import { type Content } from "@ts/sanity";
import {
  Carousel,
  CarouselScrollbar,
  CarouselSection,
  CarouselProvider,
  ContainerEscape,
  HeroProductFeature as BaseHeroProductFeature,
  HeroProductFeatureProps,
} from "@bared/ui";
import { useRichText } from "@hooks/useRichText";
import { ProductCard as ProductCardComponent } from "@components/ProductCard";

export interface HeroProductFeatureSectionProps
  extends HeroProductFeatureProps {
  products: ProductCard[];
  content: Content;
  subContent?: Content;
  textAlignment?: "left" | "center";
  verticalAlignment?: "top" | "center";
}

export default function HeroProductFeature({
  content,
  subContent,
  products,
  textAlignment = "left",
  verticalAlignment = "center",
  ...props
}: HeroProductFeatureSectionProps) {
  const { parseContent } = useRichText();

  return (
    <BaseHeroProductFeature {...props}>
      <div
        className={`flex h-full flex-col ${
          verticalAlignment === "center" ? "justify-center" : "justify-start"
        }`}
      >
        <div
          className={`mb-maxi ${
            textAlignment === "center" ? "text-center" : "text-left"
          }`}
        >
          {parseContent(content)}
        </div>

        <ContainerEscape config={{ default: "both", md: "none" }}>
          <div className="md:mx-0 -mx-micro">
            <CarouselProvider>
              <Carousel
                ariaLabel="Featured products"
                slidesPerView={{ default: "1.5", md: "2", lg: "2" }}
                hideNativeScrollbar={true}
                className={`gap-1 ${
                  products.length === 1
                    ? "justify-center md:justify-start"
                    : "justify-start md:overflow-x-visible"
                } ${
                  products.length === 1 && textAlignment === "center"
                    ? "md:justify-center"
                    : ""
                }`}
              >
                {products.map((product, index) => (
                  <CarouselSection key={product.handle} index={index}>
                    <ProductCardComponent
                      {...product}
                      index={index}
                      sizes="100vw"
                    />
                  </CarouselSection>
                ))}
              </Carousel>

              {products.length > 1 && (
                <div className="md:hidden">
                  <CarouselScrollbar />
                </div>
              )}
            </CarouselProvider>
          </div>
        </ContainerEscape>

        {subContent && (
          <div
            className={`mt-midi block ${
              textAlignment === "center" ? "text-center" : "ml-micro text-left"
            }`}
          >
            {parseContent(subContent)}
          </div>
        )}
      </div>
    </BaseHeroProductFeature>
  );
}
